import { FdeCdfSpecification } from 'features/dataLayers/generation/fdeCdfSpecification'
import { useGetGenerateAllValidSpecifiedCdfsFromBaseFn } from 'features/dataLayers/generation/useGenerateDerivedCdfData'
import {
  RolodexCacheInstance,
  RolodexEntryQueryBody,
  useRolodexEntries,
} from 'features/rolodex'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { useEffect, useMemo } from 'react'
import {
  ForecastComponentConfig,
  GeckoComponentDataFrame,
  GeckoEntryBase,
  GeckoInspection,
  RolodexEntryType,
} from 'types'

/**
 * For each unitId supplied, this function generates derived CDFs for each CDF specification and uploads them to the Rolodex Cache.
 *
 * This function uses all Thickness-CDFs in "Rolodex Proper" tagged to the unit as Base-CDFs for derived CDF generation.  If
 * you would like to include a different Base-CDF, you can use the `overrides` parameter to specify it.
 *
 * This function uses only Rolodex data to generate derived CDFs (no Rolodex-Cache) -- this is because we generate "thickness
 * derived CDFs" (e.g., Thickness-Mean), so if we used Rolodex-Cache thickness CDFs as "base-CDFs", we would generate new
 * "base CDFs" each time this runs, which would kick off generating new derived CDFs (including more "thickness CDFs"), which
 * would cause an infinite loop.  The `extraCDFsToInclude` do not need to be "Rolodex Proper" CDFs; however, that value needs to be stably-memoized
 * across runs of this function.
 *
 * @param rolodexCacheInstance - The Rolodex Cache instance to use for generating derived CDFs
 * @param cdfSpecifications - The CDF specifications to generate derived CDFs for
 * @param unitIds - The unit IDs to generate derived CDFs for
 * @param filterBaseCdfs - A function that filters the Base-CDFs to use for derived CDF generation.
 *                            NOTE: This must be memoized.
 *                            NOTE: This function is only run on the fetched CDFs, not on any `extraCDFsToInclude`.
 * @param overrides - Optional overrides for CDF-generation
 *
 *
 */
export function useInitializeDerivedCdfsFromRolodexData(
  rolodexCacheInstance: RolodexCacheInstance,
  cdfSpecifications: ReadonlyArray<FdeCdfSpecification>,
  unitIds: ReadonlyArray<string> | string,
  filterBaseCdfs: (cdf: GeckoComponentDataFrame) => boolean,
  overrides?: {
    extraCDFsToInclude?:
      | ReadonlyArray<GeckoComponentDataFrame>
      | GeckoComponentDataFrame
    /** Extra inspections to include in lookup (e.g., predicted inspections from Rolodex Cache) */
    extraInspectionsToInclude?: ReadonlyArray<GeckoInspection> | GeckoInspection
  }
) {
  const unitIdsArray = Array.isArray(unitIds) ? unitIds : [unitIds]

  const rolodexFetchParams: RolodexEntryQueryBody[] | undefined = isEmpty(
    unitIdsArray
  )
    ? undefined
    : unitIdsArray.map((curUnit) => ({
        type: [
          RolodexEntryType.FDE_FORECAST_COMPONENT_CONFIG,
          RolodexEntryType.COMPONENT_DATA_FRAME,
          RolodexEntryType.COMPONENT_DATASET,
          RolodexEntryType.ASSET_MODEL,
          RolodexEntryType.INSPECTION,
        ],
        tags: { [RolodexEntryType.UNIT]: curUnit },
        limit: 100000,
      }))

  const {
    componentDataFrames: rolodexCDFs,
    assetModels,
    inspections,
    componentDatasets,
  } = useRolodexEntries(rolodexFetchParams)

  const { entries: fccEntries } = useRolodexEntries(
    unitIdsArray.map((curUnit) => ({
      type: [RolodexEntryType.FDE_FORECAST_COMPONENT_CONFIG],
      tags: { [RolodexEntryType.UNIT]: curUnit },
      limit: 100,
    }))
  )

  const { componentDataFrames, forecastComponentConfigs } = useMemo(() => {
    const forecastComponentConfigs = fccEntries.filter(
      (entry): entry is GeckoEntryBase<ForecastComponentConfig> =>
        entry.type === RolodexEntryType.FDE_FORECAST_COMPONENT_CONFIG
    )

    const extraCDFs: GeckoComponentDataFrame[] = !overrides?.extraCDFsToInclude
      ? []
      : Array.isArray(overrides.extraCDFsToInclude)
        ? overrides.extraCDFsToInclude
        : [overrides.extraCDFsToInclude]
    const componentDataFrames = [
      ...extraCDFs,
      ...rolodexCDFs.filter(filterBaseCdfs),
    ]

    return {
      forecastComponentConfigs,
      componentDataFrames,
    }
  }, [fccEntries, overrides?.extraCDFsToInclude, rolodexCDFs, filterBaseCdfs])

  // Combine fetched inspections with any extra inspections from overrides
  const allInspections = useMemo(() => {
    const extraInspections: GeckoInspection[] =
      !overrides?.extraInspectionsToInclude
        ? []
        : Array.isArray(overrides.extraInspectionsToInclude)
          ? overrides.extraInspectionsToInclude
          : [overrides.extraInspectionsToInclude]
    return [...inspections, ...extraInspections]
  }, [inspections, overrides?.extraInspectionsToInclude])

  const allValuesPairings = useMemo(() => {
    return componentDataFrames
      .map((curCDF) => {
        const assetModelEntry = assetModels.find(
          (curAM) => curAM.tags.unit === curCDF.tags.unit
        )
        if (isNil(assetModelEntry)) {
          console.warn(
            `Attempted to create derived CDFs without an Asset-Model.  BaseCDF === `,
            curCDF
          )
          return
        }

        // Look for inspection in both Rolodex Proper and extra inspections (e.g., from Rolodex Cache)
        const inspectionEntry = allInspections.find(
          (curInspection) => curInspection.id === curCDF.tags.inspection
        )

        const forecastComponentConfigEntry = forecastComponentConfigs.find(
          (curFcc) => curFcc.tags.component === curCDF.tags.component
        )

        const componentDataset = componentDatasets.find(
          (cds) => cds.id === curCDF.tags[RolodexEntryType.COMPONENT_DATASET]
        )

        return {
          cdf: curCDF,
          inspection: inspectionEntry,
          forecastComponentConfig: forecastComponentConfigEntry?.data,
          assetModel: assetModelEntry,
          componentDataset,
        }
      })
      .filter((curPairing) => !isNil(curPairing))
  }, [
    assetModels,
    componentDataFrames,
    componentDatasets,
    forecastComponentConfigs,
    allInspections,
  ])

  const generateAllValidSpecifiedCdfsFn =
    useGetGenerateAllValidSpecifiedCdfsFromBaseFn(rolodexCacheInstance)

  useEffect(() => {
    allValuesPairings.forEach(
      ({
        cdf,
        inspection,
        forecastComponentConfig,
        assetModel,
        componentDataset,
      }) => {
        generateAllValidSpecifiedCdfsFn({
          cdfSpecifications,
          baseCDF: cdf,
          assetModel,
          forecastComponentConfig: forecastComponentConfig,
          baseInspection: inspection,
          componentDataset,
        })
      }
    )
  }, [allValuesPairings, cdfSpecifications, generateAllValidSpecifiedCdfsFn])
}
